import { createRoute, redirect } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { CreateAgencySchema, InviteAgencyAdminSchema } from '@admitly/shared';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import {
  useAgencies,
  useAgencySignups,
  useApproveSignup,
  useCreateAgency,
  useInviteAgencyAdmin,
  useReactivateAgency,
  useRejectSignup,
  useResendAgencyInvite,
  useSetComp,
  useSetTrial,
  useSuspendAgency,
  type Agency,
  type AgencySignup,
  type SubscriptionStatus,
} from '../../../../hooks/useAgencies.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/Input.js';
import { Label } from '../../../../components/ui/Label.js';
import { Textarea } from '../../../../components/ui/Textarea.js';
import { ApiError } from '../../../../lib/api.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/agencies',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'super_admin') throw redirect({ to: '/app' });
  },
  component: AgenciesPage,
});

// green / amber / red pill colours (reuses the team-list inline-style approach).
const GREEN = { backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' };
const AMBER = { backgroundColor: '#FCEFDC', color: '#B85C00', border: '1px solid #F2DCB8' };
const RED = { backgroundColor: '#FBEAEA', color: '#A83232', border: '1px solid #F0D0D0' };

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  canceled: 'Canceled',
  unpaid: 'Unpaid',
  none: 'No subscription',
};

function BillingPill({ agency }: { agency: Agency }) {
  // Comp is the headline state; the underlying Stripe status is shown as a hint.
  if (agency.is_comped) {
    return (
      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium" style={GREEN}>
        Comped (free)
      </span>
    );
  }
  const style =
    agency.subscription_status === 'active' || agency.subscription_status === 'trialing'
      ? GREEN
      : agency.subscription_status === 'past_due'
        ? AMBER
        : RED;
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium" style={style}>
      {STATUS_LABEL[agency.subscription_status]}
    </span>
  );
}

// Exported for unit testing (rendered in isolation). Not used elsewhere.
export function CompToggle({ agency }: { agency: Agency }) {
  const [confirming, setConfirming] = useState(false);
  const setComp = useSetComp();
  const next = !agency.is_comped;

  if (confirming) {
    return (
      <div className="flex items-center justify-end gap-2">
        <span className="text-xs text-muted max-w-[260px] text-left">
          {agency.is_comped
            ? 'Remove complimentary access? They’ll then need a paid subscription or be locked out.'
            : 'Grant complimentary (free) access? They won’t be billed.'}
        </span>
        <Button
          variant={agency.is_comped ? 'danger' : 'primary'}
          size="sm"
          loading={setComp.isPending}
          onClick={() =>
            setComp.mutate({ id: agency.id, isComped: next }, { onSuccess: () => setConfirming(false) })
          }
        >
          Confirm
        </Button>
        <Button variant="ghost" size="sm" disabled={setComp.isPending} onClick={() => setConfirming(false)}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant={agency.is_comped ? 'ghost' : 'secondary'}
      size="sm"
      onClick={() => setConfirming(true)}
    >
      {agency.is_comped ? 'Remove free access' : 'Make free'}
    </Button>
  );
}

/** Current trial date → a YYYY-MM-DD value for the date input (local). */
const toDateInput = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-CA') : '';

/**
 * Set / extend / clear an agency's trial window. Shows the current end (or
 * "None"/"Expired"); expands to a date input + quick presets (+14/+30 days) +
 * Clear. Grants free access until the date, locking automatically after — no
 * Stripe, no comp/subscription change. Mirrors the other inline controls.
 */
function TrialControl({ agency }: { agency: Agency }) {
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState<string>(() => toDateInput(agency.trial_ends_at));
  const [error, setError] = useState<string | null>(null);
  const setTrial = useSetTrial();
  const pending = setTrial.isPending;

  const current = agency.trial_ends_at;
  const isExpired = current ? new Date(current).getTime() < Date.now() : false;

  function reset() {
    setEditing(false);
    setDate(toDateInput(agency.trial_ends_at));
    setError(null);
  }

  async function submit(trialEndsAt: string | null) {
    setError(null);
    try {
      await setTrial.mutateAsync({ id: agency.id, trialEndsAt });
      reset();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  function saveDate() {
    if (!date) {
      setError('Pick a date or use a preset.');
      return;
    }
    submit(new Date(`${date}T23:59:59`).toISOString());
  }

  const preset = (days: number) => submit(new Date(Date.now() + days * 86_400_000).toISOString());

  if (!editing) {
    return (
      <div className="flex items-center justify-end gap-2">
        <span className={isExpired ? 'text-muted' : 'text-ink'}>
          {current ? `${isExpired ? 'Expired ' : ''}${formatDate(current)}` : 'None'}
        </span>
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
          {current ? 'Edit' : 'Set'}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <span className="text-xs text-muted max-w-[280px] text-left">
        Grants {agency.name} full access until this date with no subscription needed; it locks again
        automatically once the date passes.
      </span>
      <div className="flex items-center gap-2">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          disabled={pending}
          className="max-w-[170px]"
        />
        <Button variant="primary" size="sm" loading={pending} onClick={saveDate}>
          Save
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" disabled={pending} onClick={() => preset(14)}>
          +14 days
        </Button>
        <Button variant="secondary" size="sm" disabled={pending} onClick={() => preset(30)}>
          +30 days
        </Button>
        {current && (
          <Button variant="danger" size="sm" disabled={pending} onClick={() => submit(null)}>
            Clear
          </Button>
        )}
        <Button variant="ghost" size="sm" disabled={pending} onClick={reset}>
          Cancel
        </Button>
      </div>
      {error && (
        <div className="rounded-lg bg-danger-bg border border-danger/30 px-3 py-2 text-xs text-danger max-w-[280px] text-left">
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * Pending admin setup invite: an "Invite pending" pill + a "Resend invite"
 * inline control that rotates the token and re-sends the /signup link. Mirrors
 * CompToggle's confirm. Rendered whenever a pending admin invite exists — an
 * agency with active consultants but an expired/unaccepted admin invite still
 * needs its resend affordance.
 */
function PendingInviteCell({ agency }: { agency: Agency }) {
  const email = agency.pending_admin_invite_email;
  const [confirming, setConfirming] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const resend = useResendAgencyInvite();

  if (!email) return null;

  async function confirm() {
    setError(null);
    try {
      const res = await resend.mutateAsync(agency.id);
      setSentTo(res.email);
      setConfirming(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  return (
    <div className="mt-1 flex flex-col gap-1">
      <span
        className="inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
        style={AMBER}
      >
        Invite pending
      </span>
      {sentTo ? (
        <span className="text-xs text-muted">Setup link re-sent to {sentTo}.</span>
      ) : confirming ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted max-w-[260px]">
            Re-send the setup link to <span className="font-medium">{email}</span>? The current link
            stops working.
          </span>
          <div className="flex items-center gap-2">
            <Button variant="primary" size="sm" loading={resend.isPending} onClick={confirm}>
              Resend
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={resend.isPending}
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
          </div>
          {error && <span className="text-xs text-danger max-w-[260px]">{error}</span>}
        </div>
      ) : (
        <Button variant="ghost" size="sm" className="w-fit" onClick={() => setConfirming(true)}>
          Resend invite
        </Button>
      )}
    </div>
  );
}

/**
 * Zero-admin rescue: a red "No active admin" badge whenever the tenant has no
 * active admin, with an inline "Invite admin" action that emails a role='admin'
 * setup invite into the tenant (the /:tenantId/invite-admin endpoint). Fires on
 * the admin count alone — an agency full of consultants with nobody to run it
 * (the Aussizz shape) still shows it. Exported for unit testing (like CompToggle).
 */
export function NoActiveAdminCell({ agency }: { agency: Agency }) {
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invite = useInviteAgencyAdmin();

  if (agency.active_admin_count !== 0) return null;

  async function send() {
    setError(null);
    const parsed = InviteAgencyAdminSchema.safeParse({ email });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Enter a valid email address');
      return;
    }
    try {
      const res = await invite.mutateAsync({ id: agency.id, email: parsed.data.email });
      setSentTo(res.email);
      setInviting(false);
      setEmail('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  return (
    <div className="mt-1 flex flex-col gap-1">
      <span
        className="inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
        style={RED}
      >
        No active admin
      </span>
      {sentTo ? (
        <span className="text-xs text-muted max-w-[260px]">
          Invite sent to {sentTo}. Once they accept, grant &ldquo;Manage users&rdquo; from the
          capabilities screen if this admin will run the team.
        </span>
      ) : inviting ? (
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted max-w-[260px]">
            Email a link to set up the first admin for{' '}
            <span className="font-medium">{agency.name}</span>. They set their own password.
          </span>
          <div className="flex items-center gap-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@agency.com.au"
              disabled={invite.isPending}
              className="max-w-[220px]"
              aria-label="Admin email"
            />
            <Button variant="primary" size="sm" loading={invite.isPending} onClick={send}>
              Send invite
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={invite.isPending}
              onClick={() => {
                setInviting(false);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
          {error && <span className="text-xs text-danger max-w-[260px]">{error}</span>}
        </div>
      ) : (
        <Button variant="ghost" size="sm" className="w-fit" onClick={() => setInviting(true)}>
          Invite admin
        </Button>
      )}
    </div>
  );
}

/** Red "Suspended" pill, shown in the agency cell while suspended. */
function SuspendedBadge() {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium mt-1"
      style={RED}
    >
      Suspended
    </span>
  );
}

/**
 * Suspend / reactivate an agency. Suspending blocks all its users immediately
 * (independent of billing) and captures an optional internal reason; reactivating
 * hands control back to the billing gate (no bypass). Inline confirm + error,
 * mirroring CompToggle.
 */
function SuspendControl({ agency }: { agency: Agency }) {
  const isSuspended = Boolean(agency.suspended_at);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const suspend = useSuspendAgency();
  const reactivate = useReactivateAgency();
  const pending = suspend.isPending || reactivate.isPending;

  function reset() {
    setConfirming(false);
    setReason('');
    setError(null);
  }

  async function confirm() {
    setError(null);
    try {
      if (isSuspended) {
        await reactivate.mutateAsync(agency.id);
      } else {
        await suspend.mutateAsync({ id: agency.id, reason: reason.trim() || undefined });
      }
      reset();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  if (confirming) {
    return (
      <div className="flex flex-col items-end gap-2">
        <span className="text-xs text-muted max-w-[280px] text-left">
          {isSuspended
            ? 'Reactivate this agency? They return to their normal billing state — if unpaid they’ll see the subscribe screen, not the app.'
            : 'Suspend this agency? Every one of their users is blocked immediately until you reactivate.'}
        </span>
        {!isSuspended && (
          <Textarea
            rows={2}
            placeholder="Internal reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={pending}
            className="max-w-[280px]"
          />
        )}
        {error && (
          <div className="rounded-lg bg-danger-bg border border-danger/30 px-3 py-2 text-xs text-danger max-w-[280px] text-left">
            {error}
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            variant={isSuspended ? 'primary' : 'danger'}
            size="sm"
            loading={pending}
            onClick={confirm}
          >
            {isSuspended ? 'Confirm reactivate' : 'Confirm suspend'}
          </Button>
          <Button variant="ghost" size="sm" disabled={pending} onClick={reset}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Button
      variant={isSuspended ? 'secondary' : 'ghost'}
      size="sm"
      onClick={() => setConfirming(true)}
    >
      {isSuspended ? 'Reactivate' : 'Suspend'}
    </Button>
  );
}

const formatDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

/** One verified application: idle action buttons, expanding to an inline confirm panel. */
function PendingRow({ signup }: { signup: AgencySignup }) {
  const [mode, setMode] = useState<'idle' | 'approve' | 'reject'>('idle');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const approve = useApproveSignup();
  const reject = useRejectSignup();
  const pending = approve.isPending || reject.isPending;

  function reset() {
    setMode('idle');
    setReason('');
    setError(null);
  }

  async function confirm() {
    setError(null);
    try {
      if (mode === 'approve') {
        await approve.mutateAsync(signup.id);
      } else {
        await reject.mutateAsync({ id: signup.id, reason: reason.trim() || undefined });
      }
      reset(); // row drops off the list once the query invalidates
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  return (
    <>
      <tr className="hover:bg-cream/30 transition-colors">
        <td className="px-5 py-3.5">
          <div className="font-medium text-ink">{signup.agency_name}</div>
          <div className="text-muted text-xs">{signup.requested_slug}</div>
        </td>
        <td className="px-5 py-3.5 text-ink">{signup.admin_full_name}</td>
        <td className="px-5 py-3.5 text-muted">{signup.admin_email}</td>
        <td className="px-5 py-3.5 text-muted whitespace-nowrap">{formatDate(signup.email_verified_at)}</td>
        <td className="px-5 py-3.5 text-right">
          {mode === 'idle' && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="primary" size="sm" onClick={() => setMode('approve')}>
                Approve
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setMode('reject')}>
                Reject
              </Button>
            </div>
          )}
        </td>
      </tr>

      {mode !== 'idle' && (
        <tr className="bg-cream/40">
          <td colSpan={5} className="px-5 py-4">
            <div className="flex flex-col gap-3 max-w-2xl">
              <p className="text-sm text-ink">
                {mode === 'approve' ? (
                  <>
                    Approve <span className="font-medium">{signup.agency_name}</span>? This creates
                    their account — they&rsquo;ll be able to log in and subscribe.
                  </>
                ) : (
                  <>
                    Reject <span className="font-medium">{signup.agency_name}</span>? No account is
                    created. You can add an optional reason for the record.
                  </>
                )}
              </p>

              {mode === 'reject' && (
                <Textarea
                  rows={2}
                  placeholder="Reason (optional)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  disabled={pending}
                />
              )}

              {error && (
                <div className="rounded-lg bg-danger-bg border border-danger/30 px-3 py-2 text-sm text-danger">
                  {error}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  variant={mode === 'reject' ? 'danger' : 'primary'}
                  size="sm"
                  loading={pending}
                  onClick={confirm}
                >
                  {mode === 'approve' ? 'Confirm approval' : 'Confirm rejection'}
                </Button>
                <Button variant="ghost" size="sm" disabled={pending} onClick={reset}>
                  Cancel
                </Button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function PendingApplications() {
  const signups = useAgencySignups();
  const rows = signups.data?.signups ?? [];

  if (signups.error) {
    return (
      <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3 mb-8">
        Couldn&rsquo;t load pending applications: {(signups.error as Error).message}
      </p>
    );
  }
  if (rows.length === 0) return null; // nothing awaiting review → no section

  return (
    <section className="mb-10">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="font-serif text-xl text-ink tracking-tight">Pending applications</h2>
        <span
          className="inline-flex items-center justify-center min-w-[1.4rem] h-[1.4rem] rounded-full px-1.5 text-xs font-semibold"
          style={{ backgroundColor: '#0D2B4A', color: '#FBF8F2' }}
        >
          {rows.length}
        </span>
      </div>
      <p className="text-sm text-muted mb-4 max-w-2xl">
        Self-serve signups that have verified their email and are awaiting review. Approving
        creates the agency&rsquo;s account; rejecting frees their email to re-apply.
      </p>

      <div className="bg-white border border-line rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream/60 border-b border-line">
            <tr className="text-left text-muted text-xs uppercase tracking-wider">
              <th className="px-5 py-3 font-medium">Agency</th>
              <th className="px-5 py-3 font-medium">Applicant</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Verified</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((s) => (
              <PendingRow key={s.id} signup={s} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * Super-admin "create agency by hand" form. Validates with the shared
 * CreateAgencySchema (same as the API boundary); surfaces the 409
 * "email already in use" inline. On success the agency is created locked and the
 * admin is emailed a link to set their own password.
 */
function NewAgencyForm({ onClose }: { onClose: () => void }) {
  const create = useCreateAgency();
  const [agencyName, setAgencyName] = useState('');
  const [adminName, setAdminName] = useState('');
  const [email, setEmail] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setFieldErrors({});
    const parsed = CreateAgencySchema.safeParse({
      agency_name: agencyName,
      admin_full_name: adminName,
      email,
    });
    if (!parsed.success) {
      const errs: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (typeof key === 'string' && !errs[key]) errs[key] = issue.message;
      }
      setFieldErrors(errs);
      return;
    }
    try {
      await create.mutateAsync(parsed.data);
      setCreatedEmail(parsed.data.email);
      setAgencyName('');
      setAdminName('');
      setEmail('');
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  if (createdEmail) {
    return (
      <div className="bg-white border border-line rounded-xl p-5 mb-4 max-w-xl">
        <p className="text-sm text-ink">
          Agency created. We&rsquo;ve emailed <span className="font-medium">{createdEmail}</span> a
          link to set their password and finish setup.
        </p>
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => setCreatedEmail(null)}>
            Add another
          </Button>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-line rounded-xl p-5 mb-4 max-w-xl">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor="na-agency">Agency name</Label>
          <Input
            id="na-agency"
            value={agencyName}
            onChange={(e) => setAgencyName(e.target.value)}
            invalid={Boolean(fieldErrors.agency_name)}
            placeholder="Aussizz Wollongong"
            disabled={create.isPending}
          />
          {fieldErrors.agency_name && (
            <p className="mt-1 text-xs text-danger">{fieldErrors.agency_name}</p>
          )}
        </div>
        <div>
          <Label htmlFor="na-admin">Admin full name</Label>
          <Input
            id="na-admin"
            value={adminName}
            onChange={(e) => setAdminName(e.target.value)}
            invalid={Boolean(fieldErrors.admin_full_name)}
            placeholder="Keval Patel"
            disabled={create.isPending}
          />
          {fieldErrors.admin_full_name && (
            <p className="mt-1 text-xs text-danger">{fieldErrors.admin_full_name}</p>
          )}
        </div>
        <div>
          <Label htmlFor="na-email">Admin email</Label>
          <Input
            id="na-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={Boolean(fieldErrors.email)}
            placeholder="keval@agency.com.au"
            disabled={create.isPending}
          />
          {fieldErrors.email && <p className="mt-1 text-xs text-danger">{fieldErrors.email}</p>}
        </div>
      </div>

      <p className="mt-3 text-xs text-muted max-w-md">
        Creates the agency locked (they subscribe before using it). We email the admin a link to set
        their own password — you don&rsquo;t set it.
      </p>

      {formError && (
        <div className="mt-3 rounded-lg bg-danger-bg border border-danger/30 px-3 py-2 text-sm text-danger">
          {formError}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        <Button type="submit" size="sm" loading={create.isPending}>
          Create agency
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={create.isPending} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function AgenciesPage() {
  const agencies = useAgencies();
  const [showNew, setShowNew] = useState(false);

  return (
    <div>
      <AdminPageHeader
        title="Agencies"
        description="Every agency on the platform. Grant or remove complimentary (free) access — comped agencies are never billed or locked out."
      />

      <PendingApplications />

      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-serif text-xl text-ink tracking-tight">All agencies</h2>
        {!showNew && (
          <Button size="sm" onClick={() => setShowNew(true)}>
            + New agency
          </Button>
        )}
      </div>

      {showNew && <NewAgencyForm onClose={() => setShowNew(false)} />}

      {agencies.isLoading && <p className="text-muted">Loading…</p>}
      {agencies.error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn&rsquo;t load agencies: {(agencies.error as Error).message}
        </p>
      )}

      {agencies.data && agencies.data.agencies.length > 0 && (
        <div className="bg-white border border-line rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-cream/60 border-b border-line">
              <tr className="text-left text-muted text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-medium">Agency</th>
                <th className="px-5 py-3 font-medium">Billing status</th>
                <th className="px-5 py-3 font-medium text-right">Active seats</th>
                <th className="px-5 py-3 font-medium text-right">Comp</th>
                <th className="px-5 py-3 font-medium text-right">Trial</th>
                <th className="px-5 py-3 font-medium text-right">Access</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {agencies.data.agencies.map((a) => (
                <tr key={a.id} className="hover:bg-cream/30 transition-colors">
                  <td className="px-5 py-3.5 align-top">
                    <div className="font-medium text-ink">{a.name}</div>
                    <div className="text-muted text-xs">{a.slug}</div>
                    {a.suspended_at && <SuspendedBadge />}
                    <NoActiveAdminCell agency={a} />
                    <PendingInviteCell agency={a} />
                  </td>
                  <td className="px-5 py-3.5 align-top">
                    <BillingPill agency={a} />
                  </td>
                  <td className="px-5 py-3.5 text-right tabular-nums text-ink align-top">
                    {a.active_seats}
                  </td>
                  <td className="px-5 py-3.5 text-right align-top">
                    <CompToggle agency={a} />
                  </td>
                  <td className="px-5 py-3.5 text-right align-top">
                    <TrialControl agency={a} />
                  </td>
                  <td className="px-5 py-3.5 text-right align-top">
                    <SuspendControl agency={a} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
